import { useCallback, useEffect, useRef, useState } from "react";
import { useAgentBridge } from "../../app/AgentBridge";
import { ImportView } from "../../shared/ImportView";
import {
  fetchQmHandbuch,
  saveQmDocuments,
  saveQmInput,
  uploadQmLogo,
  type QmInput,
} from "./api";
import { buildQmPrompt, detectRechtsform } from "./prompts";
import { applyQmPrefill } from "./prefill";
import { Step1Uploads } from "./Step1Uploads";
import { Step2Form } from "./Step2Form";
import { Step3Preview } from "./Step3Preview";
import { Step4Download } from "./Step4Download";
import { StepIndicator } from "./StepIndicator";
import {
  buildQmSlug,
  clearPersistedQmState,
  loadPersistedQmState,
  persistQmState,
  type QmForm,
  type QmState,
} from "./state";

const DOC_SECTIONS: { label: string; field: keyof QmForm }[] = [
  { label: "Lebensläufe / CVs", field: "cvs" },
  { label: "Unternehmensprofil / Firmenbeschreibung", field: "companyProfile" },
  { label: "Zertifikate und Nachweise", field: "certificates" },
  { label: "Sonstige Dokumente und Notizen", field: "miscDocs" },
];

export function QmWizard() {
  const { send, subscribe } = useAgentBridge();
  const [state, setState] = useState<QmState>(loadPersistedQmState);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const pendingGenerateRef = useRef(false);
  const [generating, setGenerating] = useState(false);
  const [handbuchContent, setHandbuchContent] = useState<string>("");
  const [hint, setHint] = useState<string | null>(null);
  const [prefillBanner, setPrefillBanner] = useState<string | null>(null);

  useEffect(() => {
    persistQmState(state);
  }, [state]);

  const setStep = useCallback(
    (step: QmState["step"]) =>
      setState((prev) => {
        const maxReachedStep = (step > prev.maxReachedStep ? step : prev.maxReachedStep) as QmState["maxReachedStep"];
        return { ...prev, phase: "wizard", step, maxReachedStep };
      }),
    [],
  );

  const skipImport = useCallback(() => {
    setState((prev) => ({ ...prev, phase: "wizard" }));
  }, []);

  const jumpToImport = useCallback(() => {
    setState((prev) => ({ ...prev, phase: "import" }));
  }, []);

  const patchForm = useCallback((patch: Partial<QmForm>) => {
    setState((prev) => ({ ...prev, form: { ...prev.form, ...patch } }));
  }, []);

  const buildPayload = useCallback((form: QmForm, slug: string): QmInput => {
    const qualitaetsziele = form.qualitaetsziele
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    return {
      meta: {
        created_at: new Date().toISOString(),
        company_slug: slug,
        website_url: form.websiteUrl.trim(),
      },
      stammdaten: {
        unternehmensname: form.unternehmensname,
        rechtsform: detectRechtsform(form.unternehmensname),
        sitz_adresse: form.sitzAdresse,
        gruendung: form.gruendung,
        geschaeftsfuehrung: form.geschaeftsfuehrung,
        mitarbeitende: form.mitarbeitende,
        kurzprofil: form.kurzprofil,
      },
      beratungsfelder: {
        kern_beratungsfelder: form.kernfelder,
        ergaenzende_umsetzungsleistungen: form.umsetzung,
        nicht_angeboten: form.nichtAngeboten,
      },
      zielgruppen: {
        hauptzielgruppen: form.zielgruppen,
        branchenfokus: form.branchenfokus,
        typische_projektanlaesse: form.anlaesse,
      },
      team_qualifikation: {
        schluesselpersonen: form.personen.filter((p) => p.name_funktion.trim()),
        referenzen_projektbeispiele: form.referenzen.filter((r) => r.ziel.trim() || r.branche.trim()),
      },
      personal_infrastruktur: {
        personalgewinnung: form.personalgewinnung,
        weiterbildung: form.weiterbildung,
        kapazitaeten: form.kapazitaeten,
        infrastruktur_tools: form.infrastrukturTools,
        netzwerke_partner: form.netzwerkePartner,
      },
      beratungsprozess: {
        erstkontakt: form.erstkontakt,
        bedarfsermittlung: form.bedarfsermittlung,
        auftragsklarung: form.auftragsklarung,
        durchfuehrung: form.durchfuehrung,
        dokumentation: form.dokumentation,
        feedback_nachbetreuung: form.feedbackNachbetreuung,
      },
      qualitaetsmanagement: {
        qualitaetsziele,
        interne_kontrollen: form.interneKontrollen,
        beschwerden_abweichungen: form.beschwerdenAbweichungen,
        kvp_lessons_learned: form.kvpLessonsLearned,
        risiken_chancen: form.risikenChancen,
        interne_ueberpruefung: form.interneUeberpruefung,
      },
    };
  }, []);

  const submitGenerate = useCallback(async () => {
    const { form } = state;
    if (!form.unternehmensname.trim()) {
      setHint("Bitte gib einen Unternehmensnamen ein.");
      return;
    }
    const slug = buildQmSlug(form.unternehmensname);
    const payload = buildPayload(form, slug);

    try {
      await saveQmInput(slug, payload);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setHint("Speichern fehlgeschlagen: " + message);
      return;
    }

    const docSections = DOC_SECTIONS.map((s) => ({
      label: s.label,
      content: ((form[s.field] as string) ?? "").trim(),
    })).filter((s) => s.content);
    const hasUploadedDocs = docSections.length > 0;
    if (hasUploadedDocs) {
      const docsText = docSections.map((s) => `## ${s.label}\n\n${s.content}`).join("\n\n---\n\n");
      await saveQmDocuments(slug, docsText);
    }

    if (logoFile) {
      await uploadQmLogo(slug, logoFile);
    }

    setState((prev) => ({ ...prev, currentSlug: slug }));
    pendingGenerateRef.current = true;
    setGenerating(true);
    setHint(null);
    const prompt = buildQmPrompt({
      slug,
      websiteUrl: form.websiteUrl.trim(),
      hasUploadedDocs,
      payload,
    });
    if (!send({ type: "message", content: prompt, mode: "qm_handbuch" })) {
      pendingGenerateRef.current = false;
      setGenerating(false);
      setHint("Nicht mit dem Agenten verbunden.");
    }
  }, [state, logoFile, send, buildPayload]);

  const newHandbuch = useCallback(() => {
    clearPersistedQmState();
    setState(loadPersistedQmState());
    setLogoFile(null);
    setHandbuchContent("");
    setHint(null);
    pendingGenerateRef.current = false;
    setGenerating(false);
  }, []);

  const loadHandbuch = useCallback(async (slug: string) => {
    const content = await fetchQmHandbuch(slug);
    if (content == null) return false;
    setHandbuchContent(content);
    return true;
  }, []);

  // ── React to agent server events ────────────────────────────────────────
  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type === "import_prefill_result" && msg.mode === "qm_handbuch") {
        setState((prev) => applyQmPrefill(prev, msg.data ?? {}));
        setPrefillBanner(
          "Felder wurden aus den importierten Daten vorausgefüllt. Bitte prüfen und bei Bedarf ergänzen.",
        );
        return;
      }
      if (msg.type === "done") {
        if (!pendingGenerateRef.current) return;
        const slug = state.currentSlug;
        pendingGenerateRef.current = false;
        setGenerating(false);
        if (!slug) return;
        void (async () => {
          const ok = await loadHandbuch(slug);
          if (ok) {
            setState((prev) => ({
              ...prev,
              step: 3,
              maxReachedStep: prev.maxReachedStep < 3 ? 3 : prev.maxReachedStep,
            }));
          } else {
            setHint("qm_handbuch.md nicht gefunden. Agent hat die Datei möglicherweise nicht geschrieben.");
          }
        })();
        return;
      }
      if (msg.type === "error") {
        if (pendingGenerateRef.current) {
          pendingGenerateRef.current = false;
          setGenerating(false);
        }
        return;
      }
      if (msg.type === "file_changed" && msg.fileType === "qm_handbuch") {
        const slug = state.currentSlug;
        if (!slug) return;
        void (async () => {
          const content = await fetchQmHandbuch(slug);
          if (content == null) return;
          setHandbuchContent(content);
          setState((prev) => {
            const maxReachedStep = (prev.maxReachedStep < 3 ? 3 : prev.maxReachedStep) as QmState["maxReachedStep"];
            const step = prev.step < 3 ? 3 : prev.step;
            return { ...prev, step: step as QmState["step"], maxReachedStep };
          });
        })();
      }
    });
  }, [subscribe, state.currentSlug, loadHandbuch]);

  // Re-fetch the handbuch when navigating back to Step 3/4 after a reload.
  useEffect(() => {
    if (state.step < 3) return;
    if (handbuchContent) return;
    if (!state.currentSlug) return;
    void loadHandbuch(state.currentSlug);
  }, [state.step, state.currentSlug, handbuchContent, loadHandbuch]);

  return (
    <div className="qm-wizard">
      <StepIndicator
        phase={state.phase}
        step={state.step}
        maxReachedStep={state.maxReachedStep}
        onJumpToImport={jumpToImport}
        onJumpToStep={setStep}
      />

      {prefillBanner && (
        <div className="import-prefill-banner" role="status">
          {prefillBanner}{" "}
          <button type="button" className="link-button" onClick={() => setPrefillBanner(null)}>
            Schließen
          </button>
        </div>
      )}

      {hint && (
        <div className="info-box" role="status">
          {hint}{" "}
          <button type="button" className="link-button" onClick={() => setHint(null)}>
            Schließen
          </button>
        </div>
      )}

      {state.phase === "import" && (
        <ImportView mode="qm_handbuch" onSkip={skipImport} />
      )}
      {state.phase === "wizard" && state.step === 1 && (
        <Step1Uploads
          form={state.form}
          logoFile={logoFile}
          onChange={patchForm}
          onLogoChange={setLogoFile}
          onNext={() => setStep(2)}
        />
      )}
      {state.phase === "wizard" && state.step === 2 && (
        <Step2Form
          form={state.form}
          loading={generating}
          onChange={patchForm}
          onSubmit={() => void submitGenerate()}
        />
      )}
      {state.phase === "wizard" && state.step === 3 && (
        <Step3Preview
          handbuchContent={handbuchContent}
          slug={state.currentSlug}
          onNext={async (flush) => {
            await flush();
            setStep(4);
          }}
        />
      )}
      {state.phase === "wizard" && state.step === 4 && (
        <Step4Download
          slug={state.currentSlug}
          handbuchContent={handbuchContent}
          onNew={newHandbuch}
        />
      )}
    </div>
  );
}
